import captureError from '@disco/utils/captureError'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { User } from 'types/user'
import api from '@disco/api/server'
import { getUser } from '@disco/state-management/selectors/user'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import DocumentHeadTags from 'components/document-head-tags'
import Alert from 'components/ui/alert'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import FileSelector from 'components/ui/file-selector'
import InlineError from 'components/ui/inline-error'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import Stack from 'components/ui/stack'
import TextField from 'components/ui/text-field'
import useFuncWithConfirmation from 'hooks/use-func-with-confirmation'
const SUBJECT_OPTIONS = [
  'Question about how to use DISCO',
  'Having an issue and need help',
  'Feedback about the product',
  'Update or cancel subscription',
  'Report a bug',
  'Something else',
]
type ServerError = {
  status: string
  body: {
    status: string
    message: Record<string, string[]>
  }
}

const parseError = (error: ServerError) => {
  let result: { noInputFieldError?: string } = {}

  if (Number(error.status) === 413) {
    result.noInputFieldError = 'The uploaded files are too large'
  } else if (error.body?.message) {
    for (const key of Object.keys(error.body.message)) {
      result[key] = error.body.message[key][0]
    }
  } else {
    result.noInputFieldError = parseServerError(
      error?.body?.[0] || error?.body || error,
    )
  }

  return result
}

const ContactSupport = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user: User = useSelector(getUser)
  const [errors, setErrors] = useState<
    Record<string, string | null | undefined>
  >({})
  const hasErrors: boolean = useMemo(() => {
    return Object.keys(errors).some((key: string) => Boolean(errors[key]))
  }, [errors])
  const [formState, setFormState] = useState<{
    name: string
    from_email: string
    subject: string
    description: string
  }>({
    name: '',
    description: '',
    from_email: user.email,
    subject: '',
  })
  const [attachments, setAttachments] = useState<File[]>([])
  const [isAwaiting, setIsAwaiting] = useState<boolean>(false)
  const handleCancel = useCallback(() => {
    navigate('/')
  }, [navigate])
  const {
    closeModal,
    onConfirm,
    onCancel,
    attemptedToCall: attemptedToClose,
  } = useFuncWithConfirmation(handleCancel)
  const handleFieldChange = useCallback(
    (event: {
      target: {
        name: string
        value: string
        required: boolean
      }
    }) => {
      const { name, value, required } = event.target
      let newErrors = { ...errors }

      if (required && !value) {
        newErrors[name] = 'This field is required.'
      } else {
        newErrors[name] = null
      }

      setErrors(newErrors)
      setFormState(state => ({ ...state, [name]: value }))
    },
    [errors],
  )
  const handleSubmit = useCallback(async () => {
    let newErrors = { ...errors }
    const requiredFields = ['name', 'from_email', 'subject', 'description']
    requiredFields.forEach(field => {
      if (!formState[field]) {
        newErrors[field] = 'This field is required.'
      }
    })

    if (formState.subject === 'Other' && !formState.description) {
      newErrors.description = 'This field is required.'
    }

    setErrors(newErrors)

    if (!Object.values(newErrors).some(Boolean) && !isAwaiting) {
      try {
        setIsAwaiting(true)
        await api.contactUs({
          type: 'support',
          body: formState.name + '\n' + formState.description,
          subject: formState.subject,
          from_email: formState.from_email,
          files: attachments.length > 0 ? attachments : undefined,
        })
        navigate('/')
        dispatch(
          pushUINotification({
            level: 'success',
            message: 'Email sent!',
          }),
        )
      } catch (error) {
        setErrors(parseError(error))
        captureError(error)
      }

      setIsAwaiting(false)
    }
  }, [attachments, dispatch, errors, formState, isAwaiting])
  return (
    <ModalDialog
      title="Contact DISCO Support"
      primaryAction={{
        caption: 'Submit',
        disabled: hasErrors,
        progressCaption: 'Sending',
        progressIndicator: isAwaiting,
        onClick: handleSubmit,
      }}
      width="small"
      onCancel={closeModal}
    >
      {attemptedToClose && (
        <ModalDialog
          title="Confirm"
          primaryAction={{
            caption: 'Confirm',
            onClick: onConfirm,
          }}
          width="small"
          onCancel={onCancel}
        >
          Are you sure you want to close this form? Your contact form will not
          submitted, and changes won’t be saved.
        </ModalDialog>
      )}
      <DocumentHeadTags title="Contact Support | DISCO" />
      <Stack space="small">
        <TextField
          autoFocus
          compact={false}
          error={errors?.name}
          label="Your name"
          name="name"
          required
          onBlur={handleFieldChange}
          onChange={handleFieldChange}
        />
        <TextField
          compact={false}
          error={errors?.from_email}
          label="Your user email"
          name="from_email"
          placeholder="example@record-label.com"
          required
          value={formState.from_email}
          readOnly={Boolean(user.email)}
          disabled={Boolean(user.email)}
          onBlur={handleFieldChange}
          onChange={handleFieldChange}
        />
        <>
          <Label caption="How can we help?" required />
          <CustomDropdown
            placeholder="Select"
            options={SUBJECT_OPTIONS.map((subject: string) => ({
              label: subject,
              value: subject,
            }))}
            value={formState.subject}
            onBlur={option => {
              // react-select doesn't return input value
              if (!option?.value) {
                if (formState.subject === '') {
                  handleFieldChange({
                    target: {
                      name: 'subject',
                      value: '',
                      required: true,
                    },
                  })
                }
              }
            }}
            onChange={option => {
              const newValue = option?.value ?? ''
              handleFieldChange({
                target: {
                  name: 'subject',
                  value: newValue,
                  required: true,
                },
              })
              setErrors(errors => ({ ...errors, subject: null }))
              setFormState(state => ({ ...state, subject: newValue ?? '' }))
            }}
          />
          <InlineError error={errors?.subject} status="danger" />
        </>
        <TextField
          compact={false}
          error={errors?.description}
          label="Description"
          multiline
          name="description"
          required
          value={formState.description}
          onBlur={handleFieldChange}
          onChange={handleFieldChange}
        />
        <>
          <Label caption="Attachments" />
          <FileSelector
            caption={['Up to 5 files', 'Change files']}
            height={100}
            multiple
            limit={5}
            onDrop={setAttachments}
            dnd
          />
        </>
        {errors?.noInputFieldError && (
          <Alert status="danger" icon="warning">
            {errors?.noInputFieldError}
          </Alert>
        )}
      </Stack>
    </ModalDialog>
  )
}

export default ContactSupport
